import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { DataType, OutputParameter, WorkflowStepOutputParameterContainer } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { map } from 'rxjs/operators';
import { extractWorkflowStepIdFromReplacementTag } from '../utils';
import { DelayUntilEventTaskDefinitionId } from '../../../component-loader/constants';

export interface SelectedValues {
  container: WorkflowStepOutputParameterContainer;
  param: OutputParameter;
}

const ACTIONS_INVALID_AS_THE_DATA_ORIGIN = [
  // Delay until will not add any data to the stream if the event does not happen, which can cause "bugs"
  DelayUntilEventTaskDefinitionId,
];

@Component({
  selector: 'automata-output-parameter-selector',
  templateUrl: './output-parameter-selector.component.html',
  styleUrls: ['./output-parameter-selector.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    GalaxyTooltipModule,
  ],
})
export class OutputParameterSelectorComponent implements OnInit, OnChanges {
  @Input() disabled: boolean;
  @Input() workflowStepOutputParameterContainers: WorkflowStepOutputParameterContainer[];
  @Input() dataValueThatReferencesAPreviousStep: string;
  // It's hard to compute when the workflowStepOutputParameterContainersChange, so instead of doing that this
  // component relies on the taskDefinitionId being passed in so that it can determine that.
  @Input() taskDefinitionId: string;
  // defaultDataType is the data type that will try to be selected by default when form controls initially load
  @Input() defaultDataType: DataType;
  // Is used for filtering output params to this subset of data types, empty list will allow all params to be selectable
  @Input() supportedDataTypes: DataType[] = [];
  @Output() valueSelected: EventEmitter<SelectedValues> = new EventEmitter();

  selectedValuesFormControl: FormControl<SelectedValues> = new FormControl<SelectedValues>(null, [Validators.required]);

  workflowStepOptions: WorkflowStepOutputParameterContainer[];

  selectedWorkflowStepContainer: WorkflowStepOutputParameterContainer;

  outputParameterOptions: OutputParameter[];

  constructor(private readonly destroyRef: DestroyRef) {}

  ngOnInit(): void {
    this.selectedValuesFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((param) => {
          this.valueSelected.emit(param);
        }),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.disabled?.currentValue === true) {
      this.selectedValuesFormControl.disable({ emitEvent: false });
    }
    if (changes?.disabled?.currentValue === false) {
      this.selectedValuesFormControl.enable({ emitEvent: false });
    }

    // If any of these change, that means we need to recalculate the workflowStepOptions and the outputParameterOptions that
    // correspond to those steps. Unfortunately SimpleChanges has a terrible interface.
    if (
      changes?.dataValueThatReferencesAPreviousStep?.currentValue ||
      changes?.workflowStepOutputParameterContainers?.currentValue ||
      changes?.supportedDataTypes?.currentValue ||
      changes?.taskDefinitionId?.currentValue
    ) {
      const workflowStepOutputParameterContainers =
        changes?.workflowStepOutputParameterContainers?.currentValue || this.workflowStepOutputParameterContainers;
      const dataValueThatReferencesAPreviousStep =
        changes?.dataValueThatReferencesAPreviousStep?.currentValue || this.dataValueThatReferencesAPreviousStep;
      const supportedDataTypes = changes?.supportedDataTypes?.currentValue || this.supportedDataTypes;
      this.setupWorkflowStepDropdownOptionsAndFormControlValue(
        workflowStepOutputParameterContainers,
        dataValueThatReferencesAPreviousStep,
        supportedDataTypes,
      );
      // When the component is first initialize ngOnChanges happens before ngOnInit so the subscribe to the selecteValuesFormControl doesn't trigger
      // So we need to do that manually
      this.valueSelected.emit(this.selectedValuesFormControl.value);
    }
  }

  private setupWorkflowStepDropdownOptionsAndFormControlValue(
    containers: WorkflowStepOutputParameterContainer[],
    waitForTriggerFilterDataValue: string,
    supportedDataTypes: DataType[],
  ) {
    // Filter out steps that don't have any output params matching the supported data types
    if (supportedDataTypes?.length > 0) {
      this.workflowStepOptions = containers.reduce((acc, curr) => {
        if (ACTIONS_INVALID_AS_THE_DATA_ORIGIN.includes(curr?.workflowStep?.taskDefinitionId)) {
          // Do not include these actions in the selection
          return acc;
        }
        const filteredOutputParams = curr?.outputParameters.filter((p) => {
          return this.supportedDataTypes.includes(p?.settings?.dataType);
        });
        if (filteredOutputParams?.length > 0) {
          acc.push({
            workflowStep: curr.workflowStep,
            outputParameters: filteredOutputParams,
          });
        }
        return acc;
      }, []);
    } else {
      this.workflowStepOptions = containers;
    }
    const id = extractWorkflowStepIdFromWaitForTriggerFilterWithNoDefault(waitForTriggerFilterDataValue);
    let initiallySelectedOption = containers.find((c) => c.workflowStep.id === id);
    initiallySelectedOption = initiallySelectedOption || this.workflowStepOptions[0];

    this.updateControlsForNewlySelectedWorkflowStepContainer(initiallySelectedOption, supportedDataTypes);
  }

  isSameWorkflowStep(s1: WorkflowStepOutputParameterContainer, s2: WorkflowStepOutputParameterContainer): boolean {
    return s1?.workflowStep?.id === s2?.workflowStep?.id;
  }

  compareSelectedValues(one: SelectedValues, two: SelectedValues): boolean {
    return (
      one?.container?.workflowStep?.id === two?.container?.workflowStep?.id &&
      one?.param?.settings?.id === two?.param?.settings?.id
    );
  }

  onSelectedWorkflowStepContainerChange(container: WorkflowStepOutputParameterContainer) {
    this.updateControlsForNewlySelectedWorkflowStepContainer(container, this.supportedDataTypes);
  }

  updateControlsForNewlySelectedWorkflowStepContainer(
    container: WorkflowStepOutputParameterContainer,
    supportedDataTypes: DataType[],
  ): void {
    this.selectedWorkflowStepContainer = container;
    // Because we double write the output parameters to different ids based on the data type, we want to exclude those "extras" from the list as they are not named
    this.outputParameterOptions = this.selectedWorkflowStepContainer.outputParameters
      .filter((op) => supportedDataTypes.find((d) => d === op.settings.dataType))
      .filter((op) => op?.settings?.name);
    this.selectedValuesFormControl.patchValue(
      { container: this.selectedWorkflowStepContainer, param: this.outputParameterOptions?.[0] },
      { emitEvent: true },
    );
  }
}

function extractWorkflowStepIdFromWaitForTriggerFilterWithNoDefault(waitForTriggerFilterDataValue: string): string {
  let id = '';
  if (waitForTriggerFilterDataValue) {
    const extractedId = extractWorkflowStepIdFromReplacementTag(waitForTriggerFilterDataValue);
    id = extractedId ? extractedId : id;
  }
  return id;
}
